import type { Account, Transaction, Budget, Goal, UpcomingBill, Bill } from './types';

/**
 * Mock data for development and testing
 * This data will be replaced with actual API calls
 */

export const mockAccounts: Account[] = [
  {
    id: 'acc1',
    userId: 'user1',
    name: 'Chase Checking',
    accountType: 'checking',
    balance: 5420.12,
    availableBalance: 5420.12,
    currency: 'USD',
    institution: 'Chase',
    accountNumber: '****1234',
    isActive: true,
    syncStatus: 'success',
    lastSyncedAt: new Date().toISOString(),
    createdAt: '2024-01-15T00:00:00Z',
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'acc2',
    userId: 'user1',
    name: 'Chase Savings',
    accountType: 'savings',
    balance: 15873.45,
    availableBalance: 15873.45,
    currency: 'USD',
    institution: 'Chase',
    accountNumber: '****5678',
    isActive: true,
    syncStatus: 'success',
    lastSyncedAt: new Date().toISOString(),
    createdAt: '2024-01-15T00:00:00Z',
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'acc3',
    userId: 'user1',
    name: 'Amex Gold Card',
    accountType: 'credit_card',
    balance: -845.23,
    availableBalance: 9154.77,
    creditLimit: 10000,
    currency: 'USD',
    institution: 'American Express',
    accountNumber: '****9012',
    isActive: true,
    syncStatus: 'success',
    lastSyncedAt: new Date().toISOString(),
    createdAt: '2024-01-15T00:00:00Z',
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'acc4',
    userId: 'user1',
    name: 'BofA Travel Rewards',
    accountType: 'credit_card',
    balance: -123.45,
    availableBalance: 4876.55,
    creditLimit: 5000,
    currency: 'USD',
    institution: 'Bank of America',
    accountNumber: '****3456',
    isActive: true,
    syncStatus: 'success',
    lastSyncedAt: new Date().toISOString(),
    createdAt: '2024-01-15T00:00:00Z',
    updatedAt: new Date().toISOString(),
  },
];

export const mockTransactions: Transaction[] = [
  {
    id: 'txn1',
    userId: 'user1',
    accountId: 'acc3',
    type: 'expense',
    amount: -85.20,
    date: '2024-07-26',
    description: "Trader Joe's",
    category: 'Groceries',
    merchant: "Trader Joe's",
    isPending: false,
    createdAt: '2024-07-26T00:00:00Z',
    updatedAt: '2024-07-26T00:00:00Z',
  },
  {
    id: 'txn2',
    userId: 'user1',
    accountId: 'acc3',
    type: 'expense',
    amount: -15.99,
    date: '2024-07-26',
    description: 'Netflix',
    category: 'Entertainment',
    merchant: 'Netflix',
    isRecurring: true,
    recurringPattern: { frequency: 'monthly', interval: 1 },
    isPending: false,
    createdAt: '2024-07-26T00:00:00Z',
    updatedAt: '2024-07-26T00:00:00Z',
  },
  {
    id: 'txn3',
    userId: 'user1',
    accountId: 'acc1',
    type: 'income',
    amount: 2500.00,
    date: '2024-07-25',
    description: 'Direct Deposit',
    category: 'Income',
    merchant: 'Employer',
    isRecurring: true,
    recurringPattern: { frequency: 'biweekly', interval: 1 },
    isPending: false,
    createdAt: '2024-07-25T00:00:00Z',
    updatedAt: '2024-07-25T00:00:00Z',
  },
  {
    id: 'txn4',
    userId: 'user1',
    accountId: 'acc4',
    type: 'expense',
    amount: -45.50,
    date: '2024-07-24',
    description: 'Shell Gas',
    category: 'Transportation',
    merchant: 'Shell',
    isPending: false,
    createdAt: '2024-07-24T00:00:00Z',
    updatedAt: '2024-07-24T00:00:00Z',
  },
  {
    id: 'txn5',
    userId: 'user1',
    accountId: 'acc3',
    type: 'expense',
    amount: -17.88,
    date: '2024-07-23',
    description: 'Sweetgreen',
    category: 'Dining Out',
    merchant: 'Sweetgreen',
    isPending: false,
    createdAt: '2024-07-23T00:00:00Z',
    updatedAt: '2024-07-23T00:00:00Z',
  },
  {
    id: 'txn6',
    userId: 'user1',
    accountId: 'acc1',
    type: 'expense',
    amount: -120.75,
    date: '2024-07-22',
    description: 'PG&E',
    category: 'Utilities',
    merchant: 'PG&E',
    isRecurring: true,
    recurringPattern: { frequency: 'monthly', interval: 1 },
    isPending: false,
    createdAt: '2024-07-22T00:00:00Z',
    updatedAt: '2024-07-22T00:00:00Z',
  },
  {
    id: 'txn7',
    userId: 'user1',
    accountId: 'acc3',
    type: 'expense',
    amount: -42.11,
    date: '2024-07-21',
    description: 'Amazon.com',
    category: 'Shopping',
    merchant: 'Amazon',
    isPending: false,
    createdAt: '2024-07-21T00:00:00Z',
    updatedAt: '2024-07-21T00:00:00Z',
  },
  {
    id: 'txn8',
    userId: 'user1',
    accountId: 'acc4',
    type: 'expense',
    amount: -6.50,
    date: '2024-07-20',
    description: 'Starbucks',
    category: 'Dining Out',
    merchant: 'Starbucks',
    isPending: false,
    createdAt: '2024-07-20T00:00:00Z',
    updatedAt: '2024-07-20T00:00:00Z',
  },
  {
    id: 'txn9',
    userId: 'user1',
    accountId: 'acc3',
    type: 'expense',
    amount: -32.50,
    date: '2024-07-19',
    description: 'AMC Theatres',
    category: 'Entertainment',
    merchant: 'AMC Theatres',
    isPending: false,
    createdAt: '2024-07-19T00:00:00Z',
    updatedAt: '2024-07-19T00:00:00Z',
  },
  {
    id: 'txn10',
    userId: 'user1',
    accountId: 'acc3',
    type: 'expense',
    amount: -999.00,
    date: '2024-07-18',
    description: 'Apple.com',
    category: 'Shopping',
    merchant: 'Apple',
    isPending: false,
    createdAt: '2024-07-18T00:00:00Z',
    updatedAt: '2024-07-18T00:00:00Z',
  },
  {
    id: 'txn11',
    userId: 'user1',
    accountId: 'acc1',
    type: 'income',
    amount: 50.00,
    date: '2024-07-17',
    description: 'Venmo Payment',
    category: 'Income',
    merchant: 'Friend',
    isPending: false,
    createdAt: '2024-07-17T00:00:00Z',
    updatedAt: '2024-07-17T00:00:00Z',
  },
  {
    id: 'txn12',
    userId: 'user1',
    accountId: 'acc3',
    type: 'expense',
    amount: -200,
    date: '2024-07-16',
    description: 'Uncategorized Purchase',
    category: 'Uncategorized',
    isPending: false,
    createdAt: '2024-07-16T00:00:00Z',
    updatedAt: '2024-07-16T00:00:00Z',
  },
  {
    id: 'txn13',
    userId: 'user1',
    accountId: 'acc4',
    type: 'expense',
    amount: -50,
    date: '2024-07-15',
    description: 'Another uncategorized item',
    category: 'Uncategorized',
    isPending: false,
    createdAt: '2024-07-15T00:00:00Z',
    updatedAt: '2024-07-15T00:00:00Z',
  },
];

export const mockBudgets: Budget[] = [
  {
    id: 'bud1',
    userId: 'user1',
    name: 'July 2024 Budget',
    year: 2024,
    month: 7,
    budgetMethod: 'zero-based',
    categories: [
      { categoryId: 'cat1', categoryName: 'Groceries', budgeted: 500, spent: 385.20 },
      { categoryId: 'cat2', categoryName: 'Dining Out', budgeted: 250, spent: 124.38 },
      { categoryId: 'cat3', categoryName: 'Transportation', budgeted: 150, spent: 45.50 },
      { categoryId: 'cat4', categoryName: 'Shopping', budgeted: 400, spent: 1241.11 },
      { categoryId: 'cat5', categoryName: 'Entertainment', budgeted: 100, spent: 48.49 },
      { categoryId: 'cat6', categoryName: 'Utilities', budgeted: 200, spent: 120.75 },
    ],
    income: {
      planned: 5000,
      actual: 5050,
    },
    createdAt: '2024-07-01T00:00:00Z',
    updatedAt: new Date().toISOString(),
  },
];

export const mockGoals: Goal[] = [
  {
    id: 'goal1',
    userId: 'user1',
    name: 'Emergency Fund',
    type: 'savings',
    targetAmount: 20000,
    currentAmount: 15873.45,
    targetDate: '2025-12-31',
    priority: 1,
    linkedAccountId: 'acc2',
    status: 'active',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'goal2',
    userId: 'user1',
    name: 'Hawaii Vacation',
    type: 'savings',
    targetAmount: 5000,
    currentAmount: 1250,
    targetDate: '2025-06-01',
    priority: 2,
    status: 'active',
    createdAt: '2024-02-01T00:00:00Z',
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'goal3',
    userId: 'user1',
    name: 'Pay off Amex Card',
    type: 'debt_payoff',
    targetAmount: 845.23,
    currentAmount: 250,
    priority: 3,
    linkedAccountId: 'acc3',
    status: 'active',
    debtPayoffDetails: {
      originalBalance: 845.23,
      interestRate: 18.99,
      payoffStrategy: 'avalanche',
    },
    createdAt: '2024-03-01T00:00:00Z',
    updatedAt: new Date().toISOString(),
  },
];

export const mockUpcomingBills: UpcomingBill[] = [
  { name: 'Rent', amount: 2200, dueDate: '2024-08-01', category: 'Housing', isPaid: false },
  { name: 'Spotify', amount: 10.99, dueDate: '2024-08-05', category: 'Entertainment', isPaid: false },
  { name: 'Car Insurance', amount: 150.45, dueDate: '2024-08-10', category: 'Insurance', isPaid: false },
  { name: 'Gym Membership', amount: 40, dueDate: '2024-08-15', category: 'Health', isPaid: false },
];

export const mockBills: Bill[] = [
  {
    _id: '1',
    user: 'user-123',
    name: 'Rent',
    amount: 2200,
    currency: 'USD',
    dueDate: '2025-10-05',
    category: 'Housing',
    status: 'upcoming',
    isRecurring: true,
    recurringPattern: { frequency: 'monthly', interval: 1 },
    isPaid: false,
    reminderDays: 3,
    tags: [],
    paymentHistory: [],
    autoPayEnabled: false,
    isOverdue: false,
    daysUntilDue: 5,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    _id: '2',
    user: 'user-123',
    name: 'Electric Bill',
    amount: 120.45,
    currency: 'USD',
    dueDate: '2025-10-05',
    category: 'Utilities',
    status: 'upcoming',
    isRecurring: true,
    recurringPattern: { frequency: 'monthly', interval: 1 },
    isPaid: false,
    reminderDays: 3,
    tags: [],
    paymentHistory: [],
    autoPayEnabled: false,
    isOverdue: false,
    daysUntilDue: 5,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    _id: '3',
    user: 'user-123',
    name: 'Internet',
    amount: 79.99,
    currency: 'USD',
    dueDate: '2025-10-08',
    category: 'Utilities',
    status: 'upcoming',
    isRecurring: true,
    recurringPattern: { frequency: 'monthly', interval: 1 },
    isPaid: false,
    reminderDays: 3,
    tags: [],
    paymentHistory: [],
    autoPayEnabled: false,
    isOverdue: false,
    daysUntilDue: 8,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    _id: '4',
    user: 'user-123',
    name: 'Car Insurance',
    amount: 150.45,
    currency: 'USD',
    dueDate: '2025-10-10',
    category: 'Insurance',
    status: 'upcoming',
    isRecurring: true,
    recurringPattern: { frequency: 'monthly', interval: 1 },
    isPaid: false,
    reminderDays: 3,
    tags: [],
    paymentHistory: [],
    autoPayEnabled: false,
    isOverdue: false,
    daysUntilDue: 10,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    _id: '5',
    user: 'user-123',
    name: 'Gym Membership',
    amount: 40,
    currency: 'USD',
    dueDate: '2025-10-15',
    category: 'Health',
    status: 'upcoming',
    isRecurring: true,
    recurringPattern: { frequency: 'monthly', interval: 1 },
    isPaid: false,
    reminderDays: 3,
    tags: [],
    paymentHistory: [],
    autoPayEnabled: false,
    isOverdue: false,
    daysUntilDue: 15,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
];
